import type { PerspectiveCamera } from 'three';

import type { GraphNodeAnchor } from '../../../core/entities/graph-document';

export interface GraphCamera3DView {
	targetWorldX: number;
	targetWorldY: number;
	targetWorldZ: number;
	distance: number;
	yawRadians: number;
	pitchRadians: number;
	flyVelocityX: number;
	flyVelocityY: number;
	flyVelocityZ: number;
}

export interface GraphCamera3DViewport {
	width: number;
	height: number;
}

export interface GraphCamera3DFlyInput {
	forward: number;
	right: number;
	up: number;
	boost?: boolean;
}

export const GRAPH_CAMERA_3D_DEFAULT_DISTANCE = 1200;
export const GRAPH_CAMERA_3D_MIN_DISTANCE = 540;
export const GRAPH_CAMERA_3D_MAX_DISTANCE = 24000;
export const GRAPH_CAMERA_3D_WHEEL_SENSITIVITY = 0.0012;
export const GRAPH_CAMERA_3D_DEFAULT_YAW = -0.38;
export const GRAPH_CAMERA_3D_DEFAULT_PITCH = 0.26;
export const GRAPH_CAMERA_3D_DAMPING = 0.18;
export const GRAPH_CAMERA_3D_OVERVIEW_FOV_RADIANS = (48 * Math.PI) / 180;
export const GRAPH_CAMERA_3D_FLY_ACCELERATION = 0.012;
export const GRAPH_CAMERA_3D_FLY_MAX_SPEED = 1.0;
export const GRAPH_CAMERA_3D_FLY_FRICTION = 0.92;
export const GRAPH_CAMERA_3D_FLY_STOP_THRESHOLD = 0.001;

export function createGraphCamera3DView(): GraphCamera3DView {
	return {
		targetWorldX: 0,
		targetWorldY: 0,
		targetWorldZ: 0,
		distance: GRAPH_CAMERA_3D_DEFAULT_DISTANCE,
		yawRadians: GRAPH_CAMERA_3D_DEFAULT_YAW,
		pitchRadians: GRAPH_CAMERA_3D_DEFAULT_PITCH,
		flyVelocityX: 0,
		flyVelocityY: 0,
		flyVelocityZ: 0,
	};
}

export function panGraphCamera3DView(
	view: GraphCamera3DView,
	deltaX: number,
	deltaY: number,
	viewport: GraphCamera3DViewport,
): GraphCamera3DView {
	const baseSpan = Math.max(640, Math.min(viewport.width, viewport.height));
	const worldUnitsPerPixel = view.distance / baseSpan;
	const cosPitch = Math.cos(view.pitchRadians);
	const rightX = Math.cos(view.yawRadians);
	const rightZ = -Math.sin(view.yawRadians);
	const upX = -Math.sin(view.yawRadians) * Math.sin(view.pitchRadians);
	const upY = cosPitch;
	const upZ = -Math.cos(view.yawRadians) * Math.sin(view.pitchRadians);

	return {
		...view,
		targetWorldX: view.targetWorldX - deltaX * worldUnitsPerPixel * rightX + deltaY * worldUnitsPerPixel * upX,
		targetWorldY: view.targetWorldY + deltaY * worldUnitsPerPixel * upY,
		targetWorldZ: view.targetWorldZ - deltaX * worldUnitsPerPixel * rightZ + deltaY * worldUnitsPerPixel * upZ,
	};
}

export function orbitGraphCamera3DView(
	view: GraphCamera3DView,
	input: { deltaYaw: number; deltaPitch: number },
): GraphCamera3DView {
	return {
		...view,
		yawRadians: normalizeAngle(view.yawRadians + input.deltaYaw),
		pitchRadians: normalizeAngle(view.pitchRadians + input.deltaPitch),
	};
}

export function zoomGraphCamera3DView(
	view: GraphCamera3DView,
	deltaY: number,
	minimumDistance = GRAPH_CAMERA_3D_MIN_DISTANCE,
	maximumDistance = GRAPH_CAMERA_3D_MAX_DISTANCE,
): GraphCamera3DView {
	const zoomFactor = Math.exp(deltaY * GRAPH_CAMERA_3D_WHEEL_SENSITIVITY);

	return {
		...view,
		distance: clamp(view.distance * zoomFactor, minimumDistance, maximumDistance),
	};
}

export function flyGraphCamera3DView(
	view: GraphCamera3DView,
	input: GraphCamera3DFlyInput,
): GraphCamera3DView {
	const length = Math.hypot(input.forward, input.right, input.up);
	const hasInput = length > 0;

	const cosPitch = Math.cos(view.pitchRadians);
	const forwardX = -Math.sin(view.yawRadians) * cosPitch;
	const forwardY = -Math.sin(view.pitchRadians);
	const forwardZ = -Math.cos(view.yawRadians) * cosPitch;
	const rightX = Math.cos(view.yawRadians);
	const rightZ = -Math.sin(view.yawRadians);

	const maxSpeed = Math.max(20, view.distance * 0.028) * (input.boost ? 1.9 : 1) * GRAPH_CAMERA_3D_FLY_MAX_SPEED;
	const accel = maxSpeed * GRAPH_CAMERA_3D_FLY_ACCELERATION;

	let vx = view.flyVelocityX;
	let vy = view.flyVelocityY;
	let vz = view.flyVelocityZ;

	if (hasInput) {
		const normalizedForward = input.forward / length;
		const normalizedRight = input.right / length;
		const normalizedUp = input.up / length;

		vx += (forwardX * normalizedForward + rightX * normalizedRight) * accel;
		vy += (forwardY * normalizedForward + normalizedUp) * accel;
		vz += (forwardZ * normalizedForward + rightZ * normalizedRight) * accel;

		const currentSpeed = Math.hypot(vx, vy, vz);
		if (currentSpeed > maxSpeed) {
			const scale = maxSpeed / currentSpeed;
			vx *= scale;
			vy *= scale;
			vz *= scale;
		}
	} else {
		vx *= GRAPH_CAMERA_3D_FLY_FRICTION;
		vy *= GRAPH_CAMERA_3D_FLY_FRICTION;
		vz *= GRAPH_CAMERA_3D_FLY_FRICTION;

		if (Math.hypot(vx, vy, vz) < GRAPH_CAMERA_3D_FLY_STOP_THRESHOLD) {
			vx = 0;
			vy = 0;
			vz = 0;
		}
	}

	return {
		...view,
		targetWorldX: view.targetWorldX + vx,
		targetWorldY: view.targetWorldY + vy,
		targetWorldZ: view.targetWorldZ + vz,
		flyVelocityX: vx,
		flyVelocityY: vy,
		flyVelocityZ: vz,
	};
}

export function isFlyVelocityActive(view: GraphCamera3DView): boolean {
	return Math.hypot(view.flyVelocityX, view.flyVelocityY, view.flyVelocityZ) > GRAPH_CAMERA_3D_FLY_STOP_THRESHOLD;
}

export function decayFlyVelocity(view: GraphCamera3DView): GraphCamera3DView {
	let vx = view.flyVelocityX * GRAPH_CAMERA_3D_FLY_FRICTION;
	let vy = view.flyVelocityY * GRAPH_CAMERA_3D_FLY_FRICTION;
	let vz = view.flyVelocityZ * GRAPH_CAMERA_3D_FLY_FRICTION;

	if (Math.hypot(vx, vy, vz) < GRAPH_CAMERA_3D_FLY_STOP_THRESHOLD) {
		vx = 0;
		vy = 0;
		vz = 0;
	}

	return {
		...view,
		targetWorldX: view.targetWorldX + vx,
		targetWorldY: view.targetWorldY + vy,
		targetWorldZ: view.targetWorldZ + vz,
		flyVelocityX: vx,
		flyVelocityY: vy,
		flyVelocityZ: vz,
	};
}

export function stepGraphCamera3DView(
	view: GraphCamera3DView,
	direction: 'left' | 'right' | 'up' | 'down' | 'zoom-in' | 'zoom-out' | 'rotate-left' | 'rotate-right' | 'rotate-up' | 'rotate-down',
): GraphCamera3DView {
	const step = Math.max(56, Math.round(view.distance * 0.12));
	const rotationStep = Math.PI / 12;

	if (direction === 'zoom-in') {
		return zoomGraphCamera3DView(view, -180);
	}

	if (direction === 'zoom-out') {
		return zoomGraphCamera3DView(view, 180);
	}

	if (direction === 'rotate-left') {
		return orbitGraphCamera3DView(view, { deltaYaw: -rotationStep, deltaPitch: 0 });
	}

	if (direction === 'rotate-right') {
		return orbitGraphCamera3DView(view, { deltaYaw: rotationStep, deltaPitch: 0 });
	}

	if (direction === 'rotate-up') {
		return orbitGraphCamera3DView(view, { deltaYaw: 0, deltaPitch: rotationStep * 0.72 });
	}

	if (direction === 'rotate-down') {
		return orbitGraphCamera3DView(view, { deltaYaw: 0, deltaPitch: -rotationStep * 0.72 });
	}

	if (direction === 'left') {
		return { ...view, targetWorldX: view.targetWorldX - step };
	}

	if (direction === 'right') {
		return { ...view, targetWorldX: view.targetWorldX + step };
	}

	if (direction === 'up') {
		return { ...view, targetWorldY: view.targetWorldY + step };
	}

	return { ...view, targetWorldY: view.targetWorldY - step };
}

export function focusGraphCamera3DView(
	view: GraphCamera3DView,
	input: { worldX: number; worldY: number; worldZ: number },
): GraphCamera3DView {
	return {
		...view,
		targetWorldX: input.worldX,
		targetWorldY: input.worldY,
		targetWorldZ: input.worldZ,
	};
}

export function fitGraphCamera3DViewToAnchors(
	view: GraphCamera3DView,
	anchors: GraphNodeAnchor[],
	viewport: GraphCamera3DViewport,
): GraphCamera3DView {
	if (anchors.length === 0) {
		return createGraphCamera3DView();
	}

	let minimumX = Number.POSITIVE_INFINITY;
	let maximumX = Number.NEGATIVE_INFINITY;
	let minimumY = Number.POSITIVE_INFINITY;
	let maximumY = Number.NEGATIVE_INFINITY;
	let minimumZ = Number.POSITIVE_INFINITY;
	let maximumZ = Number.NEGATIVE_INFINITY;

	for (const anchor of anchors) {
		minimumX = Math.min(minimumX, anchor.x);
		maximumX = Math.max(maximumX, anchor.x);
		minimumY = Math.min(minimumY, -anchor.y);
		maximumY = Math.max(maximumY, -anchor.y);
		minimumZ = Math.min(minimumZ, anchor.z);
		maximumZ = Math.max(maximumZ, anchor.z);
	}

	const spanX = Math.max(260, maximumX - minimumX);
	const spanY = Math.max(220, maximumY - minimumY);
	const spanZ = Math.max(120, maximumZ - minimumZ);
	const aspect = Math.max(1, viewport.width) / Math.max(1, viewport.height);
	const distanceForHeight = (spanY * 0.5) / Math.tan(GRAPH_CAMERA_3D_OVERVIEW_FOV_RADIANS / 2);
	const distanceForWidth = (spanX * 0.5) / (Math.tan(GRAPH_CAMERA_3D_OVERVIEW_FOV_RADIANS / 2) * aspect);
	const distanceForDepth = spanZ * 1.85 + 540;
	const distance = clamp(
		Math.max(distanceForHeight, distanceForWidth, distanceForDepth, GRAPH_CAMERA_3D_DEFAULT_DISTANCE * 0.9) * 1.2,
		GRAPH_CAMERA_3D_MIN_DISTANCE,
		GRAPH_CAMERA_3D_MAX_DISTANCE,
	);

	return {
		...view,
		targetWorldX: (minimumX + maximumX) * 0.5,
		targetWorldY: (minimumY + maximumY) * 0.5,
		targetWorldZ: (minimumZ + maximumZ) * 0.5,
		distance,
	};
}

export function applyGraphCamera3DView(camera: PerspectiveCamera, view: GraphCamera3DView): void {
	const cosPitch = Math.cos(view.pitchRadians);
	const offsetX = Math.sin(view.yawRadians) * cosPitch * view.distance;
	const offsetY = Math.sin(view.pitchRadians) * view.distance;
	const offsetZ = Math.cos(view.yawRadians) * cosPitch * view.distance;

	camera.position.set(
		view.targetWorldX + offsetX,
		view.targetWorldY + offsetY,
		view.targetWorldZ + offsetZ,
	);
	camera.lookAt(view.targetWorldX, view.targetWorldY, view.targetWorldZ);
	camera.updateProjectionMatrix();
	camera.updateMatrixWorld();
}

export function dampGraphCamera3DView(
	currentView: GraphCamera3DView,
	targetView: GraphCamera3DView,
	easing = GRAPH_CAMERA_3D_DAMPING,
): GraphCamera3DView {
	return {
		targetWorldX: lerp(currentView.targetWorldX, targetView.targetWorldX, easing),
		targetWorldY: lerp(currentView.targetWorldY, targetView.targetWorldY, easing),
		targetWorldZ: lerp(currentView.targetWorldZ, targetView.targetWorldZ, easing),
		distance: lerp(currentView.distance, targetView.distance, easing),
		yawRadians: dampAngle(currentView.yawRadians, targetView.yawRadians, easing),
		pitchRadians: dampAngle(currentView.pitchRadians, targetView.pitchRadians, easing),
		flyVelocityX: lerp(currentView.flyVelocityX, targetView.flyVelocityX, easing),
		flyVelocityY: lerp(currentView.flyVelocityY, targetView.flyVelocityY, easing),
		flyVelocityZ: lerp(currentView.flyVelocityZ, targetView.flyVelocityZ, easing),
	};
}

export function isGraphCamera3DViewSettled(
	currentView: GraphCamera3DView,
	targetView: GraphCamera3DView,
	tolerance = 0.35,
): boolean {
	return (
		Math.abs(currentView.targetWorldX - targetView.targetWorldX) <= tolerance
		&& Math.abs(currentView.targetWorldY - targetView.targetWorldY) <= tolerance
		&& Math.abs(currentView.targetWorldZ - targetView.targetWorldZ) <= tolerance
		&& Math.abs(currentView.distance - targetView.distance) <= tolerance
		&& Math.abs(shortestAngleDelta(currentView.yawRadians, targetView.yawRadians)) <= 0.0025
		&& Math.abs(shortestAngleDelta(currentView.pitchRadians, targetView.pitchRadians)) <= 0.0025
	);
}

function clamp(value: number, minimum: number, maximum: number): number {
	return Math.min(maximum, Math.max(minimum, value));
}

function lerp(currentValue: number, targetValue: number, easing: number): number {
	return currentValue + (targetValue - currentValue) * easing;
}

function dampAngle(currentAngle: number, targetAngle: number, easing: number): number {
	return normalizeAngle(currentAngle + shortestAngleDelta(currentAngle, targetAngle) * easing);
}

function shortestAngleDelta(currentAngle: number, targetAngle: number): number {
	return normalizeAngle(targetAngle - currentAngle);
}

function normalizeAngle(angle: number): number {
	const fullTurn = Math.PI * 2;
	const normalized = angle % fullTurn;

	if (normalized <= -Math.PI) {
		return normalized + fullTurn;
	}

	if (normalized > Math.PI) {
		return normalized - fullTurn;
	}

	return normalized;
}
